import { useState, useCallback, useEffect } from "react";
import { X, ExternalLink } from "lucide-react";
import { MmIcon } from "../components/cc/MemoryMapIcons";

// =====================================================================
// Berichte & Dokumente — Galerie/Grid-Ansicht mit drei Reitern
// =====================================================================

interface ReportItem {
  id: string;
  title: string;
  description: string;
  url: string;
  category: "plan" | "bericht" | "session" | "referenz" | "review";
  updatedAt?: string;
  iconId?: string;
}

const REPORTS_BERICHTE: ReportItem[] = [
  {
    id: "liquid-datenquellen-2026-06-20",
    title: "Liquid — Datenquellen-Diagramm",
    description: "Interaktives Architektur-Diagramm: finAPI, LexOffice und manuelle Eingaben — Sync-Wege, PostgreSQL-Datenhaltung und Frontend-Views.",
    url: "/static/liquid-datenquellen.html",
    category: "bericht",
    updatedAt: "2026-06-20",
    iconId: "app-liquid",
  },
  {
    id: "liquid-kategorisierung-2026-05-29",
    title: "Liquid — Manuelle Kategorisierung",
    description: "Interaktives Tool: Unkategorisierte Transaktionen nach Empfänger gruppiert zuordnen. Export als JSON oder direkter Import in die Liquid-App.",
    url: "/static/liquid-kategorisierung.html",
    category: "bericht",
    updatedAt: "2026-05-29",
    iconId: "app-liquid",
  },
  {
    id: "findings-report-2026-05-25",
    title: "Findings Report — Interaktiv",
    description: "Interaktiver Findings-Report mit Checkboxen, Kategorien und Fortschritts-Tracking.",
    url: "/static/reports/findings-report.html",
    category: "bericht",
    updatedAt: "2026-05-25",
    iconId: "app-git",
  },
  {
    id: "backlog-priorisiert-2026-05-23",
    title: "Backlog — Priorisiert",
    description: "Interaktives Backlog-Dashboard: 20 Items mit Checkboxen, Kommentaren und localStorage. A/B/C Prioritäten.",
    url: "/static/Backlog-Priorisiert-2026-05-23.html",
    category: "plan",
    updatedAt: "2026-05-23",
    iconId: "app-milestone",
  },
  {
    id: "extended-review-2026-05-22",
    title: "Extended Code Review",
    description: "77 Findings: Architektur, Dependencies, Railway, Branches, Docs, Performance",
    url: "/static/extended-review-2026-05-22.html",
    category: "bericht",
    updatedAt: "2026-05-22",
    iconId: "app-git",
  },
  {
    id: "entwicklungsplan",
    title: "Entwicklungsplan",
    description: "Technischer Entwicklungsplan für alle Apps mit Sprints und Architektur-Entscheidungen.",
    url: "/static/entwicklungsplan.html",
    category: "plan",
    updatedAt: "2026-05-17",
    iconId: "app-milestone",
  },
  {
    id: "briefing-entwicklungsplan",
    title: "Briefing-App Entwicklungsplan",
    description: "Review & Verbesserungsvorschläge: Hintergrundbilder, M365, YouTube-Routine, Interaktivität.",
    url: "/static/briefing-entwicklungsplan.html",
    category: "plan",
    updatedAt: "2026-05-21",
    iconId: "app-briefing",
  },
  {
    id: "projektentwicklungsplan-dashboard",
    title: "Projektentwicklungsplan — Dashboard",
    description: "Template 02: Dashboard-Vorlage für strukturierte Projektentwicklungspläne.",
    url: "/static/projektentwicklungsplan-dashboard.html",
    category: "plan",
    updatedAt: "2026-05-21",
    iconId: "app-milestone",
  },
  {
    id: "brandfallsteuermatrix",
    title: "Brandfallsteuermatrix",
    description: "Projektentwicklungsplan: BMA/GLT-Logauswertung, Komponentenansteuerung und Endlage-Prüfung.",
    url: "/static/brandfallsteuermatrix.html",
    category: "plan",
    updatedAt: "2026-05-20",
    iconId: "app-fbb-bfsm",
  },
];

const REPORTS_DOKUMENTE: ReportItem[] = [
  {
    id: "systemlandschaft",
    title: "Systemlandschaft",
    description: "Systemarchitektur, GitHub-Repos, Entwicklungs-Roadmap und Infrastruktur.",
    url: "/static/systemlandschaft.html",
    category: "plan",
    updatedAt: "2026-07-15",
    iconId: "app-roadmap",
  },
  {
    id: "architektur-flows",
    title: "Architektur & Flows",
    description: "Interaktives Architektur-Diagramm, Konnektoren, SSO-, Deploy- und Manus-Flows.",
    url: "/static/architektur-flows.html",
    category: "referenz",
    updatedAt: "2026-05-17",
    iconId: "app-workflow",
  },
  {
    id: "design-system",
    title: "Design System",
    description: "Combsol Design System — Tokens, Komponenten und Patterns.",
    url: "/static/design-system.html",
    category: "referenz",
    updatedAt: "2026-05-17",
    iconId: "app-ds",
  },
  {
    id: "workflow-guide",
    title: "Arbeitsweise & Workflow",
    description: "Interaktiver Leitfaden: Manus Workflow, Briefing-Dateien, Arbeitsweise und Regeln.",
    url: "/static/workflow-guide.html",
    category: "referenz",
    updatedAt: "2026-05-21",
    iconId: "app-workflow",
  },
];

const CATEGORY_COLORS: Record<ReportItem["category"], string> = {
  plan: "var(--cat-violet)",
  bericht: "var(--cat-cyan)",
  session: "var(--cat-amber)",
  referenz: "var(--cat-emerald)",
  review: "var(--cat-cyan)",
};

const CATEGORY_LABELS: Record<ReportItem["category"], string> = {
  plan: "Plan",
  bericht: "Bericht",
  session: "Session",
  referenz: "Referenz",
  review: "UltraReview",
};

interface ReportTileProps {
  report: ReportItem;
  onOpen: (report: ReportItem) => void;
}

function ReportTile({ report, onOpen }: ReportTileProps) {
  return (
    <button
      key={report.id}
      className="cc-bericht-tile"
      onClick={() => onOpen(report)}
      type="button"
    >
      <div className="cc-bericht-tile__icon">
        {report.iconId
          ? <MmIcon id={report.iconId} size={40} />
          : <MmIcon id="app-docs" size={40} />
        }
      </div>
      <div className="cc-bericht-tile__content">
        <div className="cc-bericht-tile__header">
          <span
            className="cc-bericht-tile__badge"
            style={{ "--badge-color": CATEGORY_COLORS[report.category] } as React.CSSProperties}
          >
            {CATEGORY_LABELS[report.category]}
          </span>
          {report.updatedAt && (
            <span className="cc-bericht-tile__date">{report.updatedAt}</span>
          )}
        </div>
        <h3 className="cc-bericht-tile__title">{report.title}</h3>
        <p className="cc-bericht-tile__desc">{report.description}</p>
      </div>
    </button>
  );
}

type TabId = "berichte" | "reviews" | "dokumente";

export function BerichtePage() {
  const [activeTab, setActiveTab] = useState<TabId>("berichte");
  const [activeReport, setActiveReport] = useState<ReportItem | null>(null);
  const [ultraReviews, setUltraReviews] = useState<ReportItem[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  const fetchReviews = useCallback(async () => {
    setReviewsLoading(true);
    try {
      const res = await fetch("/api/cc/reviews", { credentials: "include" });
      if (!res.ok) return;
      const data = await res.json();
      const items: ReportItem[] = (data.reviews ?? []).map((r: any) => ({
        id: `review-${r.filename}`,
        title: `UltraReview: ${r.repo}`,
        description: `Code-Review vom ${r.date} — Security, Architektur, Qualität, Dependencies`,
        url: r.url,
        category: "review" as const,
        updatedAt: r.date,
        iconId: "app-git",
      }));
      setUltraReviews(items);
    } catch {
      // Silently fail
    } finally {
      setReviewsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const openReport = useCallback((report: ReportItem) => {
    setActiveReport(report);
  }, []);

  const closeReport = useCallback(() => {
    setActiveReport(null);
  }, []);

  const openExternal = useCallback((url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  const tabConfig: { id: TabId; label: string; count: number }[] = [
    { id: "berichte", label: "Berichte", count: REPORTS_BERICHTE.length },
    { id: "reviews", label: "UltraReviews", count: ultraReviews.length },
    { id: "dokumente", label: "Dokumente", count: REPORTS_DOKUMENTE.length },
  ];

  const currentItems =
    activeTab === "berichte" ? REPORTS_BERICHTE :
    activeTab === "reviews" ? ultraReviews :
    REPORTS_DOKUMENTE;

  const sectionTitle =
    activeTab === "berichte" ? "Interaktive Berichte" :
    activeTab === "reviews" ? "UltraReview-Berichte" :
    "Referenz-Dokumente";

  const sectionSubtitle =
    activeTab === "berichte" ? "HTML-Berichte und Dashboards mit Buttons, Toggles und Checkboxen" :
    activeTab === "reviews" ? "Automatisch generierte Code-Reviews — Security, Architektur, Qualität" :
    "Statische Systemdokumente — Architektur, Design System und Arbeitsweise";

  return (
    <div className="hub-page">
      <header className="hub-page-header">
        <div className="hub-page-header-main">
          <div className="hub-eyebrow">Berichte & Dokumente</div>
          <h1 className="hub-title">Repository</h1>
          <p className="hub-sub">Zentrale Galerie aller statischen Berichte, UltraReviews und Dokumentationen.</p>
        </div>
      </header>

      <div className="cc-berichte" data-testid="cc-berichte" style={{ padding: "0 24px 24px" }}>
        {/* Tab-Navigation */}
        <div
          style={{
            display: "flex",
            gap: 4,
            marginBottom: 24,
            borderBottom: "1px solid var(--border)",
            paddingBottom: 0,
          }}
        >
          {tabConfig.map(({ id, label, count }) => {
            const active = id === activeTab;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                style={{
                  padding: "8px 16px",
                  fontSize: 13,
                  fontWeight: active ? 600 : 400,
                  color: active ? "#00A3E0" : "rgba(255,255,255,0.45)",
                  background: "transparent",
                  border: "none",
                  borderBottom: active ? "2px solid #00A3E0" : "2px solid transparent",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: -1,
                  transition: "color 0.15s",
                }}
              >
                {label}
                <span
                  style={{
                    fontSize: 11,
                    padding: "1px 6px",
                    borderRadius: 10,
                    background: active ? "rgba(0,163,224,0.15)" : "rgba(255,255,255,0.06)",
                    color: active ? "#00A3E0" : "rgba(255,255,255,0.35)",
                    fontWeight: 500,
                  }}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Tab-Inhalt */}
        <section className="cc-berichte-section">
          <h2 className="cc-berichte-section__title" style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "4px", color: "var(--text)" }}>{sectionTitle}</h2>
          <p className="cc-berichte-section__subtitle" style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginBottom: "24px" }}>{sectionSubtitle}</p>

          {activeTab === "reviews" && reviewsLoading && (
            <p style={{ color: "var(--text-muted)", fontSize: 13 }}>Lade Reviews…</p>
          )}

          {activeTab === "reviews" && !reviewsLoading && ultraReviews.length === 0 && (
            <p style={{ color: "var(--text-muted)", fontSize: 13 }}>
              Noch keine UltraReview-Berichte vorhanden.
            </p>
          )}

          <div className="cc-berichte-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "16px" }}>
            {currentItems.map((report) => (
              <ReportTile key={report.id} report={report} onOpen={openReport} />
            ))}
          </div>
        </section>
      </div>

      {/* Lightbox / Fullscreen Modal */}
      {activeReport && (
        <div className="cc-lightbox" onClick={closeReport}>
          <div className="cc-lightbox__container" onClick={(e) => e.stopPropagation()}>
            <header className="cc-lightbox__header">
              <div className="cc-lightbox__title-group">
                <span
                  className="cc-bericht-tile__badge"
                  style={{ "--badge-color": CATEGORY_COLORS[activeReport.category] } as React.CSSProperties}
                >
                  {CATEGORY_LABELS[activeReport.category]}
                </span>
                <h2 className="cc-lightbox__title">{activeReport.title}</h2>
              </div>
              <div className="cc-lightbox__actions">
                <button
                  type="button"
                  className="cc-lightbox__btn"
                  onClick={() => openExternal(activeReport.url)}
                  title="In neuem Tab öffnen"
                >
                  <ExternalLink size={16} strokeWidth={1.6} />
                </button>
                <button
                  type="button"
                  className="cc-lightbox__btn"
                  onClick={closeReport}
                  title="Schließen"
                >
                  <X size={18} strokeWidth={1.8} />
                </button>
              </div>
            </header>
            <div className="cc-lightbox__body">
              <iframe
                src={activeReport.url}
                title={activeReport.title}
                className="cc-lightbox__iframe"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
